import { useState, useEffect, useRef } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { FileUp, Lock, Unlock, Trash2, Search, ChevronDown, Loader2, TrendingUp, TrendingDown, Minus, BarChart3, Plus, Filter, Package } from 'lucide-react';

export default function PatronMercuriales() {
  const [items, setItems] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comparaison, setComparaison] = useState([]);
  const [prixData, setPrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('catalogue');
  const [filterFournisseur, setFilterFournisseur] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importFournisseur, setImportFournisseur] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ produit: '', prix_unitaire: '', unite: 'kg', fournisseur: '', categorie: 'Divers' });
  const fileInputRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterFournisseur) params.fournisseur = filterFournisseur;
      if (filterCategorie) params.categorie = filterCategorie;
      const [itemsRes, fournRes, catRes] = await Promise.all([
        api.get('/mercuriales', { params }),
        api.get('/mercuriales/fournisseurs'),
        api.get('/mercuriales/categories'),
      ]);
      setItems(itemsRes.data);
      setFournisseurs(fournRes.data);
      setCategories(catRes.data);
    } catch {}
    setLoading(false);
  };

  const fetchCompare = async () => {
    try {
      const res = await api.get('/mercuriales/compare');
      setComparaison(res.data);
    } catch {}
  };

  useEffect(() => { fetchAll(); }, [filterFournisseur, filterCategorie]);
  useEffect(() => { if (activeTab === 'compare') fetchCompare(); }, [activeTab]);
  useEffect(() => {
    if (activeTab === 'factures-prix') {
      api.get('/prix-analyse').then(r => setPrixData(r.data)).catch(() => {});
    }
  }, [activeTab]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !importFournisseur.trim()) {
      toast.error('Selectionnez un fichier et un fournisseur');
      return;
    }
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/mercuriales/import?fournisseur=${encodeURIComponent(importFournisseur)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const { nb_ajoutes, nb_mis_a_jour, nb_total } = res.data;
      toast.success(`${nb_total} produits traites : ${nb_ajoutes} ajoutes, ${nb_mis_a_jour} mis a jour`);
      setShowImport(false);
      setImportFournisseur('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleBloque = async (item) => {
    try {
      await api.put(`/mercuriales/${item.id}`, { bloque: !item.bloque });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, bloque: !i.bloque } : i));
      toast.success(item.bloque ? 'Prix debloque' : 'Prix bloque');
    } catch { toast.error('Erreur'); }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/mercuriales/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Supprime');
    } catch { toast.error('Erreur'); }
  };

  const addProduit = async () => {
    if (!addForm.produit || !addForm.prix_unitaire || !addForm.fournisseur) {
      toast.error('Remplir produit, prix et fournisseur');
      return;
    }
    try {
      await api.post('/mercuriales', { ...addForm, prix_unitaire: parseFloat(addForm.prix_unitaire) });
      toast.success('Produit ajoute');
      setAddForm({ produit: '', prix_unitaire: '', unite: 'kg', fournisseur: '', categorie: 'Divers' });
      setShowAdd(false);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const filtered = items.filter(i =>
    !search || i.produit.toLowerCase().includes(search.toLowerCase()) || i.fournisseur.toLowerCase().includes(search.toLowerCase())
  );

  // Group by categorie
  const grouped = {};
  filtered.forEach(i => {
    const cat = i.categorie || 'Divers';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });

  const tabs = [
    { id: 'catalogue', label: 'Catalogue' },
    { id: 'factures-prix', label: 'Prix Factures' },
    { id: 'compare', label: 'Comparatif' },
  ];

  return (
    <div className="space-y-4" data-testid="mercuriales-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Package size={20} className="text-lemas-gold" /> Mercuriales
          </h2>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-0.5">{items.length} produits · {fournisseurs.length} fournisseurs</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} variant="outline"
            className="text-xs border-[rgb(var(--lm-border))]" data-testid="add-produit-btn">
            <Plus size={14} className="mr-1" /> Produit
          </Button>
          <Button size="sm" onClick={() => setShowImport(!showImport)}
            className="text-xs bg-lemas-gold text-[rgb(var(--lm-bg))]" data-testid="import-btn">
            <FileUp size={14} className="mr-1" /> Ajouter fournisseur
          </Button>
        </div>
      </div>

      {/* Import section */}
      {showImport && (
        <div className="lm-card p-4 border-lemas-gold/30 animate-slide-up space-y-3" data-testid="import-section">
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Ajouter un fournisseur</h3>
          <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
            Deposez la mercuriale (PDF ou Excel) — tous les produits seront importes et bloques automatiquement.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Fournisseur</label>
              <Input value={importFournisseur} onChange={e => setImportFournisseur(e.target.value)}
                placeholder="Ex: Metro, Puignau..." list="fournisseurs-list"
                className="h-8 text-sm bg-[rgb(var(--lm-bg))]" data-testid="import-fournisseur" />
              <datalist id="fournisseurs-list">
                {fournisseurs.map((f, i) => <option key={i} value={f} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Fichier</label>
              <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv" onChange={handleImport}
                disabled={importing || !importFournisseur.trim()}
                className="text-xs text-[rgb(var(--lm-text-secondary))] file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-lemas-gold/15 file:text-lemas-gold"
                data-testid="import-file" />
            </div>
          </div>
          {importing && (
            <div className="flex items-center gap-2 py-2">
              <Loader2 size={14} className="animate-spin text-lemas-gold" />
              <span className="text-xs text-[rgb(var(--lm-text-muted))]">L'IA analyse le document...</span>
            </div>
          )}
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="lm-card p-4 animate-slide-up space-y-3" data-testid="add-form">
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Ajouter un produit</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Input value={addForm.produit} onChange={e => setAddForm(f => ({ ...f, produit: e.target.value }))}
              placeholder="Produit" className="h-8 text-sm bg-[rgb(var(--lm-bg))]" data-testid="add-produit" />
            <Input type="number" step="0.01" value={addForm.prix_unitaire} onChange={e => setAddForm(f => ({ ...f, prix_unitaire: e.target.value }))}
              placeholder="Prix €" className="h-8 text-sm font-mono bg-[rgb(var(--lm-bg))]" data-testid="add-prix" />
            <select value={addForm.unite} onChange={e => setAddForm(f => ({ ...f, unite: e.target.value }))}
              className="h-8 text-sm bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-[rgb(var(--lm-text))]">
              {['kg', 'L', 'piece', 'barquette', 'botte', 'boite', 'carton'].map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <Input value={addForm.fournisseur} onChange={e => setAddForm(f => ({ ...f, fournisseur: e.target.value }))}
              placeholder="Fournisseur" list="fournisseurs-list" className="h-8 text-sm bg-[rgb(var(--lm-bg))]" data-testid="add-fournisseur" />
            <select value={addForm.categorie} onChange={e => setAddForm(f => ({ ...f, categorie: e.target.value }))}
              className="h-8 text-sm bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-[rgb(var(--lm-text))]">
              {['Viandes', 'Poissons', 'Legumes', 'Fruits', 'Epicerie', 'Boissons', 'Surgeles', 'Cremerie', 'Boulangerie', 'Divers'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Button onClick={addProduit} size="sm" className="bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] text-xs" data-testid="submit-add">
            Ajouter
          </Button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[rgb(var(--lm-bg))]">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
              activeTab === t.id ? 'bg-[rgb(var(--lm-card))] text-lemas-gold shadow-sm' : 'text-[rgb(var(--lm-text-muted))]'
            }`} data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Catalogue Tab */}
      {activeTab === 'catalogue' && (
        <div className="space-y-4 animate-fade-in">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                className="h-8 text-sm bg-[rgb(var(--lm-bg))] pl-9" data-testid="search-input" />
            </div>
            <select value={filterFournisseur} onChange={e => setFilterFournisseur(e.target.value)}
              className="h-8 text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-[rgb(var(--lm-text))]" data-testid="filter-fournisseur">
              <option value="">Tous les fournisseurs</option>
              {fournisseurs.map((f, i) => <option key={i} value={f}>{f}</option>)}
            </select>
            <select value={filterCategorie} onChange={e => setFilterCategorie(e.target.value)}
              className="h-8 text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-[rgb(var(--lm-text))]" data-testid="filter-categorie">
              <option value="">Toutes categories</option>
              {categories.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Items grouped by category */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-lemas-gold" size={24} />
            </div>
          ) : Object.keys(grouped).length > 0 ? (
            Object.entries(grouped).sort().map(([cat, catItems]) => (
              <div key={cat}>
                <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-2">
                  <Filter size={10} /> {cat} ({catItems.length})
                </h3>
                <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
                  {catItems.map(item => (
                    <div key={item.id} className="px-3 py-2.5 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.3)]" data-testid={`produit-${item.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-[rgb(var(--lm-text))] truncate">{item.produit}</p>
                          {item.bloque && <Lock size={10} className="text-lemas-gold shrink-0" />}
                        </div>
                        <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{item.fournisseur} · {item.unite}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{item.prix_unitaire.toFixed(2)}€</span>
                        <button onClick={() => toggleBloque(item)} className="p-1 rounded hover:bg-[rgb(var(--lm-bg))]" data-testid={`lock-${item.id}`}>
                          {item.bloque ? <Lock size={12} className="text-lemas-gold" /> : <Unlock size={12} className="text-[rgb(var(--lm-text-muted))]" />}
                        </button>
                        <button onClick={() => deleteItem(item.id)} className="p-1 rounded hover:bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-red))]">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-8">
              Aucun produit. Importez un PDF/Excel ou ajoutez manuellement.
            </p>
          )}
        </div>
      )}

      {/* Prix Factures Tab */}
      {activeTab === 'factures-prix' && (
        <div className="space-y-4 animate-fade-in" data-testid="prix-factures-tab">
          {prixData ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="lm-card p-3 text-center">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Produits suivis</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-text))] mt-1">{prixData.nb_produits}</p>
                </div>
                <div className="lm-card p-3 text-center">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Fournisseurs</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-text))] mt-1">{prixData.nb_fournisseurs}</p>
                </div>
              </div>

              {prixData.alertes_prix?.length > 0 && (
                <div>
                  <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-2">
                    <TrendingUp size={10} className="text-[rgb(var(--lm-red))]" /> Variations de prix detectees
                  </h3>
                  <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
                    {prixData.alertes_prix.map((a, i) => (
                      <div key={i} className="px-3 py-2.5 flex items-center justify-between" data-testid={`alerte-prix-${i}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[rgb(var(--lm-text))] truncate">{a.produit}</p>
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{a.fournisseur} · {a.date}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))] line-through">{a.ancien_prix?.toFixed(2)}€</span>
                          <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{a.nouveau_prix?.toFixed(2)}€</span>
                          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                            a.ecart_pct > 0 ? 'bg-[rgb(var(--lm-red)/0.1)] text-[rgb(var(--lm-red))]' : 'bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]'
                          }`}>
                            {a.ecart_pct > 0 ? '+' : ''}{a.ecart_pct}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Object.entries(prixData.fournisseurs || {}).sort().map(([fournisseur, produits]) => (
                <div key={fournisseur}>
                  <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-2">
                    <Package size={10} /> {fournisseur} ({produits.length} produits)
                  </h3>
                  <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
                    {produits.map((p, i) => (
                      <div key={i} className="px-3 py-2.5 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.3)]">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[rgb(var(--lm-text))] truncate">{p.nom}</p>
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                            Dern. facture: {p.derniere_facture || '—'}
                            {p.historique?.length > 1 && ` · ${p.historique.length} variations`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{p.prix_unitaire?.toFixed(2)}€/{p.unite}</span>
                          {p.montant_ht > 0 && (
                            <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">({p.montant_ht?.toFixed(2)}€)</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-lemas-gold" size={24} />
            </div>
          )}
        </div>
      )}

      {/* Comparatif Tab */}
      {activeTab === 'compare' && (
        <div className="space-y-3 animate-fade-in">
          {comparaison.length > 0 ? (
            comparaison.map((comp, i) => (
              <div key={i} className="lm-card p-3" data-testid={`compare-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-[rgb(var(--lm-text))]">{comp.produit}</h4>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                    comp.ecart_pct > 20 ? 'bg-[rgb(var(--lm-red)/0.1)] text-[rgb(var(--lm-red))]' :
                    comp.ecart_pct > 10 ? 'bg-[rgb(var(--lm-orange)/0.1)] text-[rgb(var(--lm-orange))]' :
                    'bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]'
                  }`}>
                    Ecart {comp.ecart_pct}%
                  </span>
                </div>
                <div className="space-y-1">
                  {comp.fournisseurs.map((f, j) => (
                    <div key={j} className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
                      j === 0 ? 'bg-[rgb(var(--lm-green)/0.08)]' : ''
                    }`}>
                      <div className="flex items-center gap-2">
                        {j === 0 ? <TrendingDown size={12} className="text-[rgb(var(--lm-green))]" /> : <TrendingUp size={12} className="text-[rgb(var(--lm-red))]" />}
                        <span className="text-[rgb(var(--lm-text-secondary))]">{f.fournisseur}</span>
                        {f.bloque && <Lock size={10} className="text-lemas-gold" />}
                      </div>
                      <span className={`font-mono ${j === 0 ? 'text-[rgb(var(--lm-green))] font-semibold' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                        {f.prix.toFixed(2)}€/{f.unite}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">
                  Meilleur prix : <span className="text-[rgb(var(--lm-green))]">{comp.meilleur}</span> · Economie possible : {comp.ecart.toFixed(2)}€/{comp.fournisseurs[0]?.unite}
                </p>
              </div>
            ))
          ) : (
            <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-8">
              Ajoutez le meme produit chez plusieurs fournisseurs pour comparer les prix.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
